import React from 'react';
import { BlogPost as RawBlogPost } from '../../../components/landingPage/BlogPost';
import {
    SanityBlock,
    SanityObject,
    imageUrl,
    useLpRedirect,
    useSSR,
} from '../../../lib/landingPage';
import { observer } from 'mobx-react-lite';
import { TargetBlankExternalLink, PortableText } from '../PortableText';
import { DateTime } from 'luxon';
import { Table } from '../../../components/landingPage/BlogPost/Table';
import { useSEO } from '../../../lib';
import { Image } from '../Image';
import { BlogTakeaway } from '../../../components/landingPage/BlogPost/BlogTakeaway';
import { ControllableVideo } from '../ControllableVideo/ControllableVideo';
import type { PortableTextBlock } from '@portabletext/types';
import routes from '../../../pages/landingPage/routes';
import { Banner } from '../Banner';
import { readingTime } from './formatters';
import { BlogBodyExternalLink } from '../../../components/landingPage/BlogBodyExternalLink';
import { SanityBlogPost } from './types';
import { SanityBlogPostFooter } from '../BlogPage';

export const BlogPostImage = observer(({ value }: { value: SanityBlock }) => {
    return <Image value={{ ...value, dataset: 'blog' }} preview></Image>;
});

type H2TagProps = {
    children?: React.ReactNode;
    value: PortableTextBlock;
};

// eslint-disable-next-line max-lines-per-function
export const BlogPost = ({
    post,
    footer,
}: {
    post: SanityBlogPost;
    footer: SanityBlogPostFooter;
}): JSX.Element => {
    const ssrContext = useSSR();
    useSEO(post.title, post.meta_description);
    const redirect = useLpRedirect();
    const sections = post.Content.filter(p => p.style === 'h2').map(header => ({
        title: header.children.map(c => c.text).join(''),
        key: header._key,
    }));
    ssrContext.setOpenGraphData(
        post.title,
        post.meta_description,
        imageUrl(post.imageRef.image, 'blog').url()
    );
    return (
        <RawBlogPost
            url={post.url}
            author={post.author}
            title={post.title}
            synopsis={post.synopsis}
            takeaways={post.takeaways}
            onHomepage={() => redirect(routes.blog.path)}
            relatedPosts={[
                ...(post.relatedPosts || []),
                ...(post.relatedCategoryPosts || []),
            ]}
            createdAt={DateTime.fromISO(post.dateOfWriting ?? post._createdAt)}
            sections={sections}
            cta={post.postSubmenuCTA}
            category={post.category}
            readingTime={readingTime(post)}>
            <PortableText
                value={post.Content.map(p => {
                    if (p._type === 'video') p.height = '350px';
                    return p;
                })}
                components={{
                    types: {
                        table: Table,
                        img: BlogPostImage,
                        takeaway: BlogTakeaway,
                        video: ControllableVideo,
                        banner: Banner,
                    },
                    block: {
                        h2: function Header2({ children, value }: H2TagProps) {
                            return <h2 id={value._key}>{children}</h2>;
                        },
                    },
                    marks: {
                        externalLink: TargetBlankExternalLink,
                        link: BlogBodyExternalLink,
                    },
                }}
            />
            <PortableText
                value={footer.Content.map((c: SanityBlock) => ({
                    ...c,
                    markDefs: c.markDefs.map((m: SanityObject) => ({
                        ...m,
                        postSlug: post.url,
                    })),
                }))}
                components={{
                    types: {
                        table: Table,
                        img: BlogPostImage,
                        takeaway: BlogTakeaway,
                    },
                    marks: {
                        link: BlogBodyExternalLink,
                    },
                }}
            />
        </RawBlogPost>
    );
};
